"use client";

import React from "react";
import { usePathname } from "next/navigation";
import type { Header } from "@/generated/sb";
import { NavbarDesktop } from "@repo/ui-parkmobile/NavbarDesktop";
import { NavbarMobile } from "@repo/ui-parkmobile/NavbarMobile";
import { NavbarItem } from "@repo/ui-parkmobile/types";
import { isAllowedNavatticUrl } from "@repo/utils/navattic";
import { useGlobalDataContext } from "@/components/GlobalDataContext";
import { StoryblokStory } from "@storyblok/react/rsc";
import {
  imageStoryblokResolver,
  linkResolver,
  universalLinkResolver,
} from "@/lib/storyblok/adaptors";
import {
  trackHeaderTopLevelLinkClick,
  trackHeaderCategoryClick,
  trackHeaderSubCategoryClick,
  trackHeaderLogoClick,
  trackHeaderHamburgerClick,
  trackHeaderSearch,
  trackHeaderSignInClick,
  trackCtaClick,
} from "@/utils/trackingScripts";
import {
  getActiveTopLevelItemIndex,
  getActiveSubItemIdForAllItems,
} from "@/lib/navigation";

export interface HeaderProps {
  blok: Header;
  className?: string;
}

export function Header({ blok, className = "" }: HeaderProps) {
  const { notificationBanner } = useGlobalDataContext();
  const pathname = usePathname();

  // Map Storyblok data to navbar items
  const navbarItems: NavbarItem[] =
    blok.links
      ?.map((link) => {
        if (link.component === "headerLinksGroup") {
          return {
            id: link._uid,
            title: link.groupTitle || "",
            hasDropdown: true,
            columns:
              link.columns
                ?.map((column) => {
                  return {
                    id: column._uid,
                    title: column.columnTitle || "",
                    items:
                      column.links
                        ?.map((subLink) => {
                          if (subLink.component === "headerLinksSubgroup") {
                            const linkData = linkResolver(subLink.subgroupLink);
                            const subgroupLinkData = linkResolver(
                              subLink.subgroupLink,
                            );

                            const subgroupLinkItem = subLink.subgroupLink?.[0];
                            return {
                              id: subLink._uid,
                              title: String(
                                linkData?.children ||
                                  subgroupLinkItem?.text ||
                                  subLink.subgroupTitle ||
                                  "",
                              ),
                              href: String(linkData?.href || "#"),
                              icon: subLink.icon?.[0]
                                ? (() => {
                                    const iconAsset = imageStoryblokResolver(
                                      subLink.icon[0],
                                    );
                                    return iconAsset
                                      ? {
                                          src: iconAsset.src,
                                          alt: iconAsset.alt,
                                        }
                                      : undefined;
                                  })()
                                : undefined,
                              hasSubgroup: !!(
                                subLink.links && subLink.links.length > 0
                              ),
                              subgroupTitle: subLink.subgroupTitle || "",
                              subgroupLink: String(
                                subgroupLinkData?.href || "",
                              ),
                              subgroupItems:
                                subLink.links?.map((subgroupLink) => {
                                  const subgroupLinkData = linkResolver([
                                    subgroupLink,
                                  ]);
                                  return {
                                    id: subgroupLink._uid,
                                    title: String(
                                      subgroupLinkData?.children ||
                                        subgroupLink.text ||
                                        "",
                                    ),
                                    href: String(subgroupLinkData?.href || "#"),
                                  };
                                }) || [],
                            };
                          } else if (subLink.component === "linkWithIcon") {
                            const linkData = linkResolver(subLink.link);
                            const linkItem = subLink.link?.[0];
                            return {
                              id: subLink._uid,
                              title: String(
                                linkData?.children || linkItem?.text || "",
                              ),
                              href: String(linkData?.href || "#"),
                              icon: subLink.icon?.[0]
                                ? (() => {
                                    const iconAsset = imageStoryblokResolver(
                                      subLink.icon[0],
                                    );
                                    return iconAsset
                                      ? {
                                          src: iconAsset.src,
                                          alt: iconAsset.alt,
                                        }
                                      : undefined;
                                  })()
                                : undefined,
                            };
                          }
                          return null;
                        })
                        .filter(
                          (item): item is NonNullable<typeof item> =>
                            item !== null,
                        ) || [],
                  };
                })
                .filter(
                  (column): column is NonNullable<typeof column> =>
                    column !== null,
                ) || [],
          };
        } else if (link.component === "link") {
          const linkData = linkResolver([link]);
          return {
            id: link._uid,
            title: String(linkData?.children || link.text || ""),
            href: String(linkData?.href || "#"),
          };
        }
        return null;
      })
      .filter((item): item is NonNullable<typeof item> => item !== null) || [];

  const logoImage = blok.logo?.[0];
  const logo = imageStoryblokResolver(logoImage);

  // Handle signInLink which can be either Link or Button (or multiple)
  const signInItems = blok.signInLink
    ?.map((item) => {
      if (item.component === "link") {
        const linkData = linkResolver([item]);
        const text = String(linkData?.children || item.text || "Sign in");
        const href = String(linkData?.href || "");

        return linkData
          ? {
              text,
              href,
              target: linkData.target,
              variant: "link" as const,
              onClick: () => {
                trackHeaderSignInClick({
                  ctaText: text,
                  href,
                });
              },
            }
          : null;
      } else if (item.component === "button") {
        const text = item.text || "Download app";

        // Storyblok `button.type === "navattic"`: no link, the CTA opens a demo.
        // Without a valid URL there is nothing to render in the header.
        if (item.type === "navattic") {
          if (!isAllowedNavatticUrl(item.navatticUrl)) return null;
          const navatticUrl = item.navatticUrl;
          const navatticTitle =
            typeof item.navatticTitle === "string" && item.navatticTitle
              ? item.navatticTitle
              : text;
          const navatticMobileUrl = isAllowedNavatticUrl(item.navatticMobileUrl)
            ? item.navatticMobileUrl
            : undefined;
          // Visitors silently get the desktop demo; editors are told why, as in
          // buttonResolver. `_editable` exists only on draft content and, unlike
          // the server-only isPreviewMode(), is available in this client component.
          const hasInvalidMobileUrl =
            !navatticMobileUrl &&
            typeof item.navatticMobileUrl === "string" &&
            item.navatticMobileUrl.trim() !== "" &&
            Boolean(item._editable);

          return {
            text: hasInvalidMobileUrl
              ? `${text} (invalid mobile Navattic URL)`
              : text,
            href: "",
            variant: "button" as const,
            buttonVariant: item.variant || "primary",
            buttonSize: item.size || "medium",
            navatticUrl,
            navatticTitle,
            navatticMobileUrl,
            onClick: () => {
              trackCtaClick({
                event: "ctaClick",
                section: "Header",
                config: "button",
                ctaText: text,
                contentDescription: "",
                link: navatticUrl,
              });
            },
          };
        }

        const buttonLink = universalLinkResolver(item.link);

        return buttonLink
          ? {
              text,
              href: String(buttonLink.href || ""),
              target: buttonLink.target,
              variant: "button" as const,
              buttonVariant: item.variant || "primary",
              buttonSize: item.size || "medium",
              onClick: () => {
                trackCtaClick({
                  event: "download_app_click",
                  section: "Header",
                  config: "button",
                  ctaText: text,
                  contentDescription: "",
                  link: String(buttonLink.href || ""),
                });
              },
            }
          : null;
      }
      return null;
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);

  // Keep backward compatibility with single sign-in link
  const signInItem = blok.signInLink?.[0];
  const signInData = signInItem
    ? signInItem.component === "link"
      ? linkResolver([signInItem])
      : signInItem.component === "button"
        ? (() => {
            const buttonLink = universalLinkResolver(signInItem.link);
            return buttonLink
              ? {
                  href: buttonLink.href,
                  children: signInItem.text || "Sign in",
                  target: buttonLink.target,
                }
              : null;
          })()
        : null
    : null;

  const logoLink = universalLinkResolver(blok.logoLink);
  const logoHref = logoLink?.href || "/";

  const handleLogoClick = () => {
    trackHeaderLogoClick({
      logoName: logo?.alt || "ParkMobile",
      destinationURL: logoHref,
    });
  };

  const handleHamburgerClick = () => {
    trackHeaderHamburgerClick();
  };

  const handleSearchClick = () => {
    trackHeaderSearch({ searchKeyword: "" });
  };

  const handleSignInClick = () => {
    trackHeaderSignInClick({
      ctaText: String(signInData?.children || "Sign in"),
      href: String(signInData?.href || ""),
    });
  };

  const handleTopLevelLinkClick = (innerText: string, href: string) => {
    trackHeaderTopLevelLinkClick({ innerText, href });
  };

  const handleCategoryLinkClick = (
    innerText: string,
    href: string,
    headerCategory: string,
  ) => {
    trackHeaderCategoryClick({ innerText, href, headerCategory });
  };

  const handleSubCategoryLinkClick = (
    innerText: string,
    href: string,
    headerCategory: string,
  ) => {
    trackHeaderSubCategoryClick({ innerText, href, headerCategory });
  };

  const activeTopLevelItemId = getActiveTopLevelItemIndex(
    navbarItems,
    pathname,
  );
  const activeSubItemId = getActiveSubItemIdForAllItems(navbarItems, pathname);

  const commonProps = {
    logo: logo
      ? {
          src: logo.src,
          alt: logo.alt,
          width: logo.width || 163,
          height: logo.height || 22,
        }
      : undefined,
    logoHref,
    items: navbarItems,
    activeTopLevelItemId,
    activeSubItemId,
    pathname,
    onSearchClick: handleSearchClick,
    onSignInClick: handleSignInClick,
    onLogoClick: handleLogoClick,
    onHamburgerClick: handleHamburgerClick,
    onTopLevelLinkClick: handleTopLevelLinkClick,
    onCategoryLinkClick: handleCategoryLinkClick,
    onSubCategoryLinkClick: handleSubCategoryLinkClick,
    signInText: String(signInData?.children || "Sign in"),
    signInHref: String(signInData?.href || ""),
    signInItems: signInItems?.length ? signInItems : undefined,
    className,
  };

  return (
    <>
      {notificationBanner && (
        <StoryblokStory
          story={notificationBanner}
          bridgeOptions={{
            resolveLinks: "url",
          }}
        />
      )}
      <NavbarDesktop {...commonProps} className="hidden lg:block" />
      <NavbarMobile {...commonProps} className="uipm:block uipm:lg:hidden" />
    </>
  );
}
